/* global procurementEmailMembers */

import React, { useEffect, useMemo, useState } from 'react';

import CreatableSelect from 'react-select/creatable';

import { buildLockedRecipients, buildMemberOptions } from 'Helpers/approvalRecipients';

import LockedRecipientChips from 'CommonComponents/LockedRecipientChips';

/**
 * The "who else gets the approved email" picker, shared by the requisition and
 * expenditure-approval bodies.
 *
 * Kept rather than simplified away: dropping it would silently change who is
 * notified when an approver clears their queue from Home instead of from the
 * module screen. The requestor and the chain's approvers are always included and
 * are shown as locked chips, so the field only ever adds.
 */
export default function RecipientsField({ enabled, requestor, approvers, defaultRecipients, onChange }) {
    /* SEEDED from the event's configured approval-email recipients, which is what
       the modal this replaces did. They are event-level configuration naming who
       should be copied on an approval email; starting empty would silently stop
       sending it to them. */
    const [recipients, setRecipients] = useState(defaultRecipients || []);

    const members = typeof procurementEmailMembers !== 'undefined' ? procurementEmailMembers : [];
    const memberOptions = useMemo(() => buildMemberOptions(members), [members]);

    const lockedRecipients = useMemo(
        () =>
            buildLockedRecipients({
                requestor,
                requestorRole: $t('requestor'),
                approvers,
                approverRole: $t('approver'),
            }),
        [requestor, approvers],
    );

    useEffect(() => {
        setRecipients(defaultRecipients || []);
    }, [defaultRecipients]);

    useEffect(() => {
        // Gated on `enabled`: the modal this replaces attached recipients only
        // when the event's approval email was on, so emitting the seeded list
        // here would post recipients for an email that is never sent.
        onChange?.(enabled ? recipients : []);
    }, [recipients, enabled]);

    if (!enabled) {
        return null;
    }

    return (
        <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-1.5">
            <label className="text-input__label">{$t('Email recipients')}</label>
            <p className="tw-mb-0 tw-text-sm tw-text-charcoal">{$t('Requestor and approvers are included automatically.')}</p>
            <LockedRecipientChips recipients={lockedRecipients} />
            <CreatableSelect
                isMulti
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder={$t('Add email addresses')}
                options={memberOptions}
                value={recipients.map((email) => ({ label: email, value: email }))}
                onChange={(selected) => setRecipients((selected || []).map((option) => option.value))}
                formatCreateLabel={(input) => `${$t('Add')} "${input}"`}
            />
        </div>
    );
}
